import React, { useEffect, useState } from 'react';
import { Button, Chip, Grid, IconButton, LinearProgress, Paper, Tab, Tabs, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Skeleton } from '@material-ui/lab';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import EditIcon from '@material-ui/icons/Edit';
import DeleteIcon from '@material-ui/icons/Delete';
import ReplayIcon from '@material-ui/icons/Replay';
import API_Service, { axiosDefaultConfig, axiosPutConfig, axiosURIencode } from '../../../Services/API_Service';
import { req_announcement_list, req_announcement_status } from '../../../Helper/Constants';
import LinearLoading from '../../../Components/LinearLoading';
import Alert from '../../../Components/Alert';
import { getClientDisplayName } from '../announcementUtils';

const TAB_FILTERS = ['', 'published', 'draft'];

const useStyles = makeStyles((theme) => ({
	statsRow: {
		marginBottom: theme.spacing(2)
	},
	statsCard: {
		borderRadius: 10,
		padding: theme.spacing(2),
		minHeight: 76,
		cursor: 'pointer',
		border: '1px solid transparent',
		transition: 'all 0.2s ease',
		'&:hover': {
			transform: 'translateY(-1px)',
			boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
		}
	},
	statsCardTotal: {
		backgroundColor: 'rgba(254, 242, 0, 0.08)'
	},
	statsCardPublished: {
		backgroundColor: 'rgba(46, 125, 50, 0.12)'
	},
	statsCardDraft: {
		backgroundColor: 'rgba(184, 184, 184, 0.10)'
	},
	statsCardHighlight: {
		boxShadow: 'inset 0 0 0 2px #FEF200'
	},
	statsCardLabel: {
		fontSize: 11,
		fontWeight: 500,
		textTransform: 'uppercase',
		letterSpacing: 0.5,
		color: theme.palette.text.secondary
	},
	statsCardCount: {
		fontSize: 28,
		fontWeight: 700
	},
	tabs: {
		marginBottom: theme.spacing(1)
	},
	tableWrapper: {
		'& .MuiPaper-root': {
			borderRadius: 10,
			boxShadow: 'none',
			border: `1px solid ${theme.palette.divider}`
		}
	},
	priorityChip: {
		fontWeight: 500,
		fontSize: 11,
		borderRadius: 6,
		height: 22,
		marginRight: theme.spacing(1)
	},
	priorityInfo: {
		backgroundColor: 'rgba(184, 184, 184, 0.15)'
	},
	priorityImportant: {
		backgroundColor: 'rgba(254, 242, 0, 0.2)',
		color: '#c9b800'
	},
	priorityCritical: {
		backgroundColor: 'rgba(255, 94, 91, 0.13)',
		color: '#FF5E5B'
	},
	retractBtn: {
		color: '#ED6C02',
		borderColor: '#ED6C02'
	},
	audienceChip: {
		fontSize: 10,
		height: 20,
		marginRight: theme.spacing(0.5),
		marginBottom: 2
	},
	allChip: {
		fontSize: 10,
		height: 20,
		backgroundColor: '#FEF300',
		color: '#000',
		fontWeight: 700
	},
	rateCell: {
		minWidth: 110
	},
	rateBar: {
		height: 5,
		borderRadius: 3,
		marginBottom: 4
	},
	rateText: {
		fontSize: 11,
		color: theme.palette.text.secondary
	},
	skeletonCard: {
		borderRadius: 10
	},
	skeletonTable: {
		marginTop: theme.spacing(2),
		borderRadius: 10
	}
}));

export default function ManageTab({ language, client, onEdit }) {
	const classes = useStyles();
	const { enqueueSnackbar } = useSnackbar();

	const [loading, setLoading] = useState(true);
	const [tableLoading, setTableLoading] = useState(false);
	const [list, setList] = useState([]);
	const [tab, setTab] = useState(0);
	const [confirm, setConfirm] = useState({ open: false, message: '', actionLabel: '', onConfirm: null });

	const snack = (msg, type = 'success') => {
		enqueueSnackbar(msg, {
			variant: type,
			preventDuplicate: true,
			autoHideDuration: type === 'success' ? 2000 : 3000
		});
	};

	useEffect(() => {
		if (client) {
			loadList(true);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [client]);

	const loadList = (initial = false) => {
		if (!initial) {
			setTableLoading(true);
		}
		const formdata = new FormData();
		API_Service.post(req_announcement_list, formdata, axiosDefaultConfig(client)).then(({ data }) => {
			if (parseInt(data.result) === 1) {
				setList(data.extraData || []);
			} else {
				setList([]);
				snack(data.message || 'Error', 'error');
			}
			setLoading(false);
			setTableLoading(false);
		}).catch(() => {
			snack('Network error', 'error');
			setLoading(false);
			setTableLoading(false);
		});
	};

	const updateStatus = (id, action) => {
		const params = axiosURIencode({ id, action });
		API_Service.put(req_announcement_status, params, axiosPutConfig(client)).then(({ data }) => {
			if (parseInt(data.result) === 1) {
				snack(data.message);
				loadList();
			} else {
				snack(data.message || 'Error', 'error');
			}
		}).catch(() => snack('Network error', 'error'));
	};

	const askRetract = (row) => {
		setConfirm({
			open: true,
			message: language.announcement_retract_confirm,
			actionLabel: language.announcement_retract,
			onConfirm: () => updateStatus(row.id, 'retract')
		});
	};

	const askDelete = (row) => {
		setConfirm({
			open: true,
			message: language.announcement_delete_confirm,
			actionLabel: language.delete,
			onConfirm: () => updateStatus(row.id, 'delete')
		});
	};

	const displayed = tab === 0 ? list : list.filter(r => r.status === TAB_FILTERS[tab]);
	const stats = [
		{ label: language.total, count: list.length, cardClass: classes.statsCardTotal },
		{ label: language.announcement_published, count: list.filter(r => r.status === 'published').length, cardClass: classes.statsCardPublished },
		{ label: language.announcement_drafts, count: list.filter(r => r.status === 'draft').length, cardClass: classes.statsCardDraft }
	];

	const attachCount = (row) => {
		try {
			return (JSON.parse(row.attachment || '[]') || []).length;
		} catch (e) {
			return 0;
		}
	};

	const rowFromMeta = (tableMeta) => {
		// rowData[4] = the 'id' column; survives table sorting unlike rowIndex
		const rowId = tableMeta.rowData[4];
		return displayed.find(r => String(r.id) === String(rowId));
	};

	const priorityLabel = (p) => {
		if (p === 'important') return language.announcement_priority_important;
		if (p === 'critical') return language.announcement_priority_critical;
		return language.announcement_priority_info;
	};

	const priorityClass = (p) => {
		if (p === 'important') return classes.priorityImportant;
		if (p === 'critical') return classes.priorityCritical;
		return classes.priorityInfo;
	};

	const columns = [
		{
			name: 'title',
			label: language.announcement_title,
			options: {
				display: true,
				customBodyRender: (value, tableMeta) => {
					const row = rowFromMeta(tableMeta);
					if (!row) return value;
					const count = attachCount(row);
					return (
						<div>
							<Chip
								size="small"
								label={priorityLabel(row.priority)}
								className={`${classes.priorityChip} ${priorityClass(row.priority)}`}
							/>
							{row.status === 'draft' && (
								<Chip size="small" label={language.announcement_draft} className={classes.priorityChip} />
							)}
							<Typography variant="body2">{value}</Typography>
							{count > 0 && (
								<Typography className={classes.rateText}>{`${count} ${language.announcement_attachments}`}</Typography>
							)}
						</div>
					);
				}
			}
		},
		{
			name: 'clients',
			label: language.announcement_audience,
			options: {
				display: true,
				customBodyRender: (value, tableMeta) => {
					const row = rowFromMeta(tableMeta);
					if (!row) return null;
					const userMap = {};
					(row.users || []).forEach((u) => {
						userMap[u.client] = (userMap[u.client] || 0) + 1;
					});
					const chipLabel = (code) => (
						userMap[code] ? `${getClientDisplayName(code)} (${userMap[code]})` : getClientDisplayName(code)
					);
					if (row.audience_type === 'all') {
						return (
							<div>
								<Chip size="small" label={language.announcement_all_clients} className={classes.allChip} />
								{Object.keys(userMap).map((code) => (
									<Chip key={code} size="small" label={chipLabel(code)} variant="outlined" className={classes.audienceChip} />
								))}
							</div>
						);
					}
					return (
						<div>
							{(row.clients || []).map((code) => (
								<Chip key={code} size="small" label={chipLabel(code)} variant="outlined" className={classes.audienceChip} />
							))}
						</div>
					);
				}
			}
		},
		{
			name: 'publish_date',
			label: language.announcement_publish_date,
			options: {
				display: true,
				customBodyRender: (value) => {
					if (value && value !== '') {
						return moment(value, 'YYYY-MM-DD').format('DD/MM/YYYY');
					}
					return '-';
				}
			}
		},
		{
			name: 'read_count',
			label: language.announcement_read_rate,
			options: {
				display: true,
				customBodyRender: (value, tableMeta) => {
					const row = rowFromMeta(tableMeta);
					if (!row || row.status !== 'published') {
						return <Typography variant="body2">-</Typography>;
					}
					const total = parseInt(row.recipient_count) || 0;
					const read = parseInt(row.read_count) || 0;
					const pct = total > 0 ? Math.round((read / total) * 100) : 0;
					return (
						<div className={classes.rateCell}>
							<LinearProgress variant="determinate" value={pct} className={classes.rateBar} />
							<Typography className={classes.rateText}>{`${read}/${total} (${pct}%)`}</Typography>
						</div>
					);
				}
			}
		},
		{
			name: 'id',
			label: language.announcement_actions,
			options: {
				display: true,
				customBodyRender: (value, tableMeta) => {
					const row = rowFromMeta(tableMeta);
					if (!row) return null;
					if (row.status === 'published') {
						return (
							<Button
								size="small"
								variant="outlined"
								className={classes.retractBtn}
								startIcon={<ReplayIcon />}
								onClick={() => askRetract(row)}>
								{language.announcement_retract}
							</Button>
						);
					}
					return (
						<>
							<IconButton size="small" onClick={() => onEdit(row)}>
								<EditIcon fontSize="small" />
							</IconButton>
							<IconButton size="small" onClick={() => askDelete(row)}>
								<DeleteIcon fontSize="small" />
							</IconButton>
						</>
					);
				}
			}
		}
	];

	const options = {
		selectableRows: 'none',
		responsive: 'standard',
		rowsPerPage: 20,
		rowsPerPageOptions: [20, 50, 100],
		download: false,
		print: false,
		filter: false,
		search: true
	};

	if (loading) {
		return (
			<div>
				<Grid container spacing={2}>
					{[0, 1, 2].map(i => (
						<Grid item xs={6} sm={4} key={i}>
							<Skeleton animation="wave" variant="rect" height={76} className={classes.skeletonCard} />
						</Grid>
					))}
				</Grid>
				<Skeleton animation="wave" height={300} className={classes.skeletonTable} />
			</div>
		);
	}

	return (
		<div>
			<Grid container spacing={2} className={classes.statsRow}>
				{stats.map((stat, index) => (
					<Grid item xs={6} sm={4} key={stat.label}>
						<Paper
							elevation={0}
							className={`${classes.statsCard} ${stat.cardClass} ${tab === index ? classes.statsCardHighlight : ''}`}
							onClick={() => setTab(index)}>
							<Typography className={classes.statsCardLabel}>{stat.label}</Typography>
							<Typography className={classes.statsCardCount}>{stat.count}</Typography>
						</Paper>
					</Grid>
				))}
			</Grid>

			<Tabs
				className={classes.tabs}
				indicatorColor="primary"
				textColor="inherit"
				value={tab}
				onChange={(e, v) => setTab(v)}>
				<Tab label={language.announcement_all} />
				<Tab label={language.announcement_published} />
				<Tab label={language.announcement_drafts} />
			</Tabs>

			{tableLoading && <LinearLoading />}
			<div className={classes.tableWrapper}>
				<MUIDataTable
					title={language.announcement_manage}
					data={displayed}
					columns={columns}
					options={options}
				/>
			</div>

			<Alert
				setPositive={() => setConfirm(prev => ({ ...prev, open: false }))}
				setNegative={() => {
					if (confirm.onConfirm) {
						confirm.onConfirm();
					}
					setConfirm(prev => ({ ...prev, open: false }));
				}}
				setClose={() => setConfirm(prev => ({ ...prev, open: false }))}
				open={confirm.open}
				title={language.announcement}
				message={confirm.message}
				positive={language.cancel}
				negative={confirm.actionLabel}
				autoDispossal={true}
			/>
		</div>
	);
}
